import { Outlet, Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Mail, Phone, MapPin } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_green-creations/artifacts/cf3sam7y_Untitled%20design.png";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/birdpots", label: "Birdpots" },
  { to: "/furniture", label: "Furniture" },
  { to: "/structures", label: "Structures" },
  { to: "/conversions", label: "Conversions" },
  { to: "/contact", label: "Contact" },
];

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen flex flex-col" data-testid="app-layout">
      {/* Header */}
      <header
        className="sticky top-0 z-50 border-b border-[#D5CFBF]"
        style={{ backgroundColor: "#EAE5D9" }}
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img
              src={LOGO_URL}
              alt="Small Feet"
              className="h-14 w-auto object-contain rounded"
              data-testid="logo-image"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`nav-link font-body text-sm tracking-widest uppercase transition-colors ${
                  location.pathname === link.to
                    ? "text-[#8B5E34] active"
                    : "text-[#3A5A3F] hover:text-[#222522]"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden text-[#3A5A3F]"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Nav */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.nav
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden overflow-hidden border-t border-[#D5CFBF]"
              style={{ backgroundColor: "#EAE5D9" }}
              data-testid="mobile-nav"
            >
              <div className="px-6 pb-6 flex flex-col gap-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                    onClick={() => setMobileOpen(false)}
                    className={`font-body text-sm tracking-widest uppercase ${
                      location.pathname === link.to
                        ? "text-[#8B5E34]"
                        : "text-[#3A5A3F]"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer
        className="py-16 md:py-24"
        style={{ backgroundColor: "#3A5A3F" }}
        data-testid="main-footer"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
            {/* Logo & Tagline */}
            <div>
              <img
                src={LOGO_URL}
                alt="Small Feet"
                className="h-16 w-auto object-contain mb-4"
              />
              <p className="text-[#F9F8F5]/50 text-sm font-body leading-relaxed mt-4">
                Rescuing forgotten materials and turning them into things 
                people actually want. Based in Leicester, powered by tea.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-heading text-[#F9F8F5] text-lg mb-4">Explore</h4>
              <div className="flex flex-col gap-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="text-[#F9F8F5]/60 hover:text-[#8B5E34] text-sm font-body transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-heading text-[#F9F8F5] text-lg mb-4">Get in Touch</h4>
              <div className="flex flex-col gap-3">
                <a
                  href="mailto:smallfeet@mtloop.co.uk"
                  className="flex items-center gap-3 text-[#F9F8F5]/60 hover:text-[#8B5E34] text-sm font-body transition-colors"
                  data-testid="footer-email"
                >
                  <Mail size={16} />
                  smallfeet@mtloop.co.uk
                </a>
                <a
                  href="tel:07979119771"
                  className="flex items-center gap-3 text-[#F9F8F5]/60 hover:text-[#8B5E34] text-sm font-body transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone size={16} />
                  07979 119 771
                </a>
                <div className="flex items-center gap-3 text-[#F9F8F5]/60 text-sm font-body">
                  <MapPin size={16} />
                  Leicester, UK
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-12 pt-8 border-t border-[#F9F8F5]/10">
            <p
              className="text-[#F9F8F5]/40 text-xs font-body text-center"
              data-testid="footer-trading-name"
            >
              Small Feet is a trading name of Mt. Loop Ltd.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
